import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

interface VitalsConfig {
  endpoint?: string;
  debug?: boolean;
}

class WebVitalsMonitor {
  private config: VitalsConfig;
  private metrics: Map<string, Metric> = new Map();

  constructor(config: VitalsConfig = {}) {
    this.config = {
      debug: import.meta.env.DEV,
      ...config
    };
  }

  private handleMetric = (metric: Metric) => {
    this.metrics.set(metric.name, metric);

    if (this.config.debug) {
      console.warn(`[Web Vitals] ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
        id: metric.id,
        navigationType: metric.navigationType,
      });
    }

    // Send to analytics endpoint if configured
    if (this.config.endpoint) {
      this.sendToAnalytics(metric);
    }

    // Store in sessionStorage for debugging
    try {
      const vitals = JSON.parse(sessionStorage.getItem('web-vitals') || '{}');
      vitals[metric.name] = {
        value: metric.value,
        rating: metric.rating,
        timestamp: Date.now(),
      };
      sessionStorage.setItem('web-vitals', JSON.stringify(vitals));
    } catch (error) {
      console.warn('Failed to store web vitals in sessionStorage:', error);
    }
  };

  private async sendToAnalytics(metric: Metric) {
    try {
      await fetch(this.config.endpoint!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: metric.name,
          value: metric.value,
          rating: metric.rating,
          id: metric.id,
          navigationType: metric.navigationType,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        }),
      });
    } catch (error) {
      console.warn('Failed to send web vitals to analytics:', error);
    }
  }

  public init() {
    try {
      onCLS(this.handleMetric);
      onINP(this.handleMetric);
      onFCP(this.handleMetric);
      onLCP(this.handleMetric);
      onTTFB(this.handleMetric);

      if (this.config.debug) {
        console.warn('[Web Vitals] Monitoring initialized');
      }
    } catch (error) {
      console.warn('Failed to initialize web vitals monitoring:', error);
    }
  }

  public getMetrics(): Record<string, Metric> {
    return Object.fromEntries(this.metrics);
  }

  public getPerformanceReport(): {
    vitals: Record<string, Metric>;
    navigation: PerformanceNavigationTiming | null;
    resources: PerformanceResourceTiming[];
  } {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

    return {
      vitals: this.getMetrics(),
      navigation,
      resources: resources.slice(0, 50), // Limit to first 50 resources
    };
  }

  public getRating(metricName: string): 'good' | 'needs-improvement' | 'poor' | 'unknown' {
    const metric = this.metrics.get(metricName);
    return metric?.rating || 'unknown';
  }

  public isPerformanceGood(): boolean {
    const criticalMetrics = ['CLS', 'INP', 'LCP'];
    return criticalMetrics.every(metric => {
      const rating = this.getRating(metric);
      return rating === 'good' || rating === 'unknown';
    });
  }
}

// Export singleton instance
export const webVitals = new WebVitalsMonitor();

// Initialize on module load if in browser
if (typeof window !== 'undefined') {
  // Wait for page load to ensure all metrics can be captured
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => webVitals.init());
  } else {
    webVitals.init();
  }
}

export default webVitals;